<template>
  <div :style="root_style">
    <div class="text-center">
      <!-- callers that embed this chart inside their own card (the dashboard
           widgets) pass no title, and this block still reserved a 22px bold
           line plus the row's margins above every one of them. -->
      <div class="row" v-if="graph_title || graph_desc">
        <div class="col-12" style="font-weight:bold; font-size: 22px; color: #545454; margin-left: 0px">
          {{graph_title}}
        </div>
        {{graph_desc}}
      </div>
      <div style="margin-top: 40px" v-if="!loading && !chart_data">
        <h3>-no data available-</h3>
      </div>
    </div>
    <div :style="wrap_style">
      <GChart
          v-if="!loading && chart_data"
          :class="{ 'db-chart-fill': fill_height }"
          :resizeDebounce="500"
          :type="chart_type"
          :data="chart_data"
          :options="gchart_options"
          :settings="{ packages: ['corechart'] }"
      />
      <div v-if="loading" style="height:150px; margin-top:260px">
        <div class="text-center text-primary">
          <b-spinner class="align-middle"></b-spinner>
          <strong style="margin-left:20px">Loading...</strong>
        </div>
      </div>
    </div>
  </div>

</template>

<script>
import {GChart} from 'vue-google-charts'

// google charts default their svg text to Arial regardless of the page font;
// hand them the app's own stack (App.vue body) so charts match the product
const CHART_FONT = 'Avenir, Helvetica, Arial, sans-serif'

// frozen fallback for callers that pass no chart_colors (admin_dashboard,
// sms_usage_dashboard): this is the pre-2026-08-01 TYPE_COLOR_PALETTE order.
// the shared palette in utils/alerts_analytics was re-ordered for the newer
// normal-vision cvd floor, and importing it here would silently recolor
// those untouched staff pages on deploy.
const DEFAULT_COLORS = [
  '#2a78d6', '#1baf7a', '#eda100', '#008300', '#4a3aa7', '#e34948', '#e87ba4', '#eb6834',
]

export default {

  components: {
    GChart,
  },
  props: {
    chart_legend: {type: Object, required: false},
    chart_data: {type: Array, required: false},
    chart_type: {type: String, required: true},
    loading: {type: Boolean, required: true},
    graph_title: {type: String, required: true},
    graph_desc: {type: String, required: false},
    graph_height: {type: String, required: true},
    view_window: {type: Object, required: false},
    date_format: {type: String, required: true},
    chart_area: {type: Object, required: false, default: null},
    chart_colors: {type: Array, required: false, default: null},
    curved: {type: Boolean, required: false, default: false},
    stacked: {type: Boolean, required: false, default: false},
    // the default negative margins below are tuned to bleed a full-width chart
    // past its card padding; that bleed overflows badly when the chart shares
    // a row with a sibling instead of taking the full row to itself.
    narrow: {type: Boolean, required: false, default: false},
    // stretches to fill its grid/flex cell instead of using a fixed pixel
    // height, so it lines up with a sibling of different chrome (title/padding)
    // without having to hand-tune matching pixel values
    fill_height: {type: Boolean, required: false, default: false},
    // drops the card border/padding/top-margin for embedding inside a caller
    // that already provides its own card chrome (e.g. a dashboard widget),
    // so the chart isn't double-boxed. Existing callers don't pass this, so
    // their bordered look is unaffected.
    borderless: {type: Boolean, required: false, default: false},
  },

  computed: {
    is_bar_chart() {
      return this.chart_type === 'BarChart' || this.chart_type === 'ColumnChart'
    },
    // google charts draw text in svg from js options, so css tokens can't
    // reach them; mirror the theme inks here (a muted slate under light
    // themes rather than full black, so axes stop shouting over the data)
    chart_ink() {
      return this.$store.state.profile.site_theme === 'midnight' ? '#d7e1ea' : '#3d4b5c'
    },
    // soft reference lines for the value axis; hairline-adjacent in both themes
    grid_ink() {
      return this.$store.state.profile.site_theme === 'midnight' ? '#2c3b4a' : '#e9eef3'
    },
    root_style() {
      if (this.borderless) {
        return this.fill_height ? 'height: 100%; display: flex; flex-direction: column;' : ''
      }
      const base = 'border: solid; border-color: black; border-width: 1px; border-radius: 25px; padding: 10px;'
      if (this.fill_height) return `${base} margin-top: 0; height: 100%; display: flex; flex-direction: column;`
      return `${base} margin-top: 30px;`
    },
    wrap_style() {
      const bar_margin = this.narrow ? 'margin: 0;' : 'margin: 0 10px;'
      const margin = this.is_bar_chart ? bar_margin : (this.narrow ? 'margin: 0;' : 'margin-left: -80px; margin-right: -100px;')
      if (this.fill_height) return `${margin} flex: 1; min-height: 0; display: flex;`
      return margin
    },
    gchart_options() {
      const ink = this.chart_ink
      const opts = {
        ...(this.fill_height ? {} : { height: this.graph_height }),
        fontName: CHART_FONT,
        titleTextStyle: {
          color: ink,
          fontSize: 27,
          bold: false,
          italic: false
        },
        // narrow = embedded in a half-width dashboard card. google charts
        // sizes the legend and the axis gutters for a full-page chart, which
        // in a ~456px card left the plot area occupying under a third of the
        // card and paginated the legend behind a "1/2" pager. smaller legend
        // and axis type, plus maxLines, lets the series fit on one page and
        // hands the reclaimed room to the plot.
        legend: this.narrow
          ? { position: 'top', alignment: 'center', maxLines: 2, textStyle: { color: ink, fontSize: 10 } }
          : { position: 'top', alignment: 'center', textStyle: { color: ink } },
        backgroundColor: { fill: 'transparent' },
        lineWidth: 2,
        ...(this.curved ? { curveType: 'function' } : {}),
        ...(this.stacked ? { isStacked: true } : {}),
        // default palette is the same CVD-validated categorical set the alerts
        // widgets use, replacing the old named-css list (black/orange/green/...)
        colors: this.chart_colors || DEFAULT_COLORS,
        hAxis: {
          // for BarChart the horizontal axis carries the values, so it gets
          // the soft reference lines; on time/category axes lines are noise
          gridlines: { color: this.chart_type === 'BarChart' ? this.grid_ink : 'transparent' },
          minorGridlines: { color: 'transparent' },
          textStyle: { color: ink, ...(this.narrow ? { fontSize: 10 } : {}) },
          titleTextStyle: { color: ink, italic: false, fontSize: 18 },
          // baselineColor takes a color STRING, not an object like its
          // gridlines/textStyle neighbors -- the object form was silently
          // discarded, so the baseline fell back to google's near-black and
          // vanished on the midnight canvas
          baselineColor: ink,
          ...(this.narrow ? { slantedText: false } : {}),
          ...(this.is_bar_chart ? {} : { format: this.date_format })
        },
        ...(this.chart_type !== 'BarChart' && this.chart_type !== 'ColumnChart'
          ? { explorer: { actions: ['dragToZoom'], axis: 'horizontal', keepInBounds: true } }
          : {}),
        vAxis: {
          gridlines: { color: this.chart_type === 'BarChart' ? 'transparent' : this.grid_ink },
          minorGridlines: { color: 'transparent' },
          textStyle: { color: ink, ...(this.narrow ? { fontSize: 10 } : {}) },
          titleTextStyle: { color: ink, italic: false, fontSize: 18 },
          // see hAxis above: string, not object
          baselineColor: ink,
          ...(this.chart_type !== 'BarChart' && this.chart_type !== 'ColumnChart'
            ? { viewWindowMode: 'explicit', viewWindow: this.vw_window }
            : {})
        },
        titlePosition: { color: ink },
        crosshair: { color: ink }
      }
      // google's default chartArea reserves a full-page chart's worth of
      // gutter. a narrow chart has to claim its plot area explicitly or it
      // keeps drawing a small picture in a large frame; an explicit
      // chart_area from the caller still wins over this default.
      if (this.narrow) opts.chartArea = { left: 38, top: 30, right: 12, bottom: 30 }
      if (this.chart_area) opts.chartArea = this.chart_area
      return opts
    },
  },

  data() {
    return {
      vw_window: null,
    };
  },
  created() {
    this.vw_window = this.view_window
    this.check_range()
  },
  methods: {
    async update_view_window() {
      if (this.vw_window == null)
        this.vw_window = this.view_window
      else
        this.vw_window = null
      this.check_range()
    },
    check_range() {
      if (this.chart_data && this.vw_window) {
        var max = Math.max(...this.chart_data.filter((_, i) => i > 0).map(i => i[1]))
        var min = Math.min(...this.chart_data.filter((_, i) => i > 0).map(i => i[1]))

        if (min < this.vw_window.min) {
          this.vw_window.min = min
        }
        if (max > this.vw_window.max) {
          this.vw_window.max = max
        }
      }
    }
  }
}

</script>

<style scoped>
/* applied to GChart's root element via class attribute inheritance, so the
   chart canvas actually fills the flex:1 wrapper instead of collapsing to 0 */
.db-chart-fill {
  width: 100%;
  height: 100%;
}
</style>

